import React from 'react';
import { useFeatureFlags } from '../../../contexts/FeatureFlagsContext';
import { AlertTriangle, Settings } from 'lucide-react';

export const FeatureSettings = () => {
  const { features, updateFeature, isLoading, error } = useFeatureFlags();

  if (isLoading) {
    return <div>Lädt...</div>;
  }

  if (!updateFeature) {
    return (
      <div className="rounded-md bg-yellow-50 dark:bg-yellow-900/50 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <AlertTriangle className="h-5 w-5 text-yellow-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
              Zugriff verweigert
            </h3>
            <p className="mt-2 text-sm text-yellow-700 dark:text-yellow-200">
              Sie haben keine Berechtigung, diese Einstellungen zu ändern.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const handleToggle = async (feature: keyof typeof features, enabled: boolean) => {
    try {
      await updateFeature(feature, enabled);
    } catch (err) {
      console.error('Error toggling feature:', err);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <Settings className="h-5 w-5 mr-2" />
          Funktionen
        </h3>
        
        <div className="mt-6 space-y-6">
          {error && (
            <div className="rounded-md bg-red-50 dark:bg-red-900/50 p-4">
              <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex-grow flex flex-col">
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  Buchungssystem
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Aktiviert oder deaktiviert das komplette Buchungssystem
                </span>
              </div>
              <button
                onClick={() => handleToggle('bookingSystem', !features.bookingSystem)}
                className={`
                  relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
                  transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
                  ${features.bookingSystem ? 'bg-indigo-600' : 'bg-gray-200'}
                `}
              >
                <span
                  className={`
                    pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 
                    transition duration-200 ease-in-out
                    ${features.bookingSystem ? 'translate-x-5' : 'translate-x-0'}
                  `}
                />
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex-grow flex flex-col">
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  Benachrichtigungen
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Aktiviert oder deaktiviert das Benachrichtigungssystem
                </span>
              </div>
              <button
                onClick={() => handleToggle('notifications', !features.notifications)}
                className={`
                  relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
                  transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
                  ${features.notifications ? 'bg-indigo-600' : 'bg-gray-200'}
                `}
              >
                <span
                  className={`
                    pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 
                    transition duration-200 ease-in-out
                    ${features.notifications ? 'translate-x-5' : 'translate-x-0'}
                  `}
                />
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex-grow flex flex-col">
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  Reitbeteiligungen
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Aktiviert oder deaktiviert das Reitbeteiligungssystem
                </span>
              </div>
              <button
                onClick={() => handleToggle('coRiderSystem', !features.coRiderSystem)}
                className={`
                  relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
                  transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
                  ${features.coRiderSystem ? 'bg-indigo-600' : 'bg-gray-200'}
                `}
              >
                <span
                  className={`
                    pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 
                    transition duration-200 ease-in-out
                    ${features.coRiderSystem ? 'translate-x-5' : 'translate-x-0'}
                  `}
                />
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex-grow flex flex-col">
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  Öffentliche Profile
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Aktiviert oder deaktiviert öffentliche Pferdeprofile
                </span>
              </div>
              <button
                onClick={() => handleToggle('publicProfiles', !features.publicProfiles)}
                className={`
                  relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
                  transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
                  ${features.publicProfiles ? 'bg-indigo-600' : 'bg-gray-200'}
                `}
              >
                <span
                  className={`
                    pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 
                    transition duration-200 ease-in-out
                    ${features.publicProfiles ? 'translate-x-5' : 'translate-x-0'}
                  `}
                />
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex-grow flex flex-col">
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  Wartungsmodus
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Aktiviert den Wartungsmodus (nur Administratoren haben Zugriff)
                </span>
              </div>
              <button
                onClick={() => handleToggle('maintenanceMode', !features.maintenanceMode)}
                className={`
                  relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
                  transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
                  ${features.maintenanceMode ? 'bg-indigo-600' : 'bg-gray-200'}
                `}
              >
                <span
                  className={`
                    pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 
                    transition duration-200 ease-in-out
                    ${features.maintenanceMode ? 'translate-x-5' : 'translate-x-0'}
                  `}
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};